import { Grid, List, Typography } from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import { Box } from '@mui/system';

import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { dataGridCol } from '@shared/components/grid/common';
import { Modal } from '@shared/components/modals/Modal';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { DealsButton } from '@styles/StyledButtons';
import { CalculatorListItem, GridContentWrapper } from '@styles/GridStyles';
import { FieldDivWrapper } from '@styles/StyledDiv';
import { colorTheme } from '@styles/muiTheme';
import { formatCurrency } from '@shared/commonFunctions';
import { calculateAmorizationDetailer, calculateInrestRateNotApr } from '../queries';
import { IAmorizationDetailer, IAmorizationDetailerResponse, IAmortSchedules, ICalculator, IntrestRateCalc } from '../interfaces';
import { amortizationDetailerValidation, interestRateCalculatorValidation } from '../validation';
import { CalculatorForm } from './CalculatorForm';

const columns: GridColDef[] = [
  dataGridCol('month'),
  dataGridCol('payment', {
    valueFormatter: ({ value }) => formatCurrency(value)
  }),
  dataGridCol('principal', {
    valueFormatter: ({ value }) => formatCurrency(value)
  }),
  dataGridCol('interest', {
    valueFormatter: ({ value }) => formatCurrency(value)
  }),
  dataGridCol('additionalPrincipal', {
    valueFormatter: ({ value }) => formatCurrency(value)
  }),
  dataGridCol('balance', {
    valueFormatter: ({ value }) => formatCurrency(value)
  }),
].map((column) => ({ flex: 1, ...column }));

const amortizationIntitialValues = {
  amortFinanced: null,
  amortTerm: null,
  amortApr: null,
};

const amortizationDetailerFormConfig = [
  {
    name: 'amortFinanced',
    label: 'Amt Financed',
    prefix: '$',
    decimalScale: null,
    component: NumberInputField,
  },
  {
    name: 'amortTerm',
    label: 'Term',
    component: NumberInputField,
  },
  {
    name: 'amortApr',
    label: 'APR',
    suffix: '%',
    decimalScale: null,
    component: NumberInputField,
  },
  {
    name: 'amortExtra',
    label: 'Extra principal ',
    prefix: '$',
    decimalScale: null,
    component: NumberInputField,
  }
];

const inrestRateCalcInitialValues = {
  amountFinanced: null,
  term: null,
  paymentAmount: null,
};

const intrestRateCalcNotAprFormConfig = [
  {
    name: 'amountFinanced',
    label: 'Amt Financed',
    prefix: '$',
    decimalScale: null,
    component: NumberInputField,
  },
  {
    name: 'term',
    label: 'Term',
    component: NumberInputField,
  },
  {
    name: 'paymentAmount',
    label: 'Payment',
    prefix: '$',
    decimalScale: null,
    component: NumberInputField,
  },
];

export const CalculatorModal = ({ calculatorModalPosition, onClose }: ICalculator) => {
  const [intrestRateData, setIntrestRateData] = useState<number>();
  const [amortizationDetailerData, setAmortizationDetailerData] = useState<IAmorizationDetailerResponse>();
  const [isIntrestCalcLoading, setIsIntrestCalcLoading] = useState(false);
  const [isAmortizationLoading, setIsAmrotizationLoading] = useState(false);
  const handleInrestRateCalculates = useCallback(async (data: IntrestRateCalc) => {
    setIsIntrestCalcLoading(true);
    const resp: number = await calculateInrestRateNotApr(data);
    setIntrestRateData(resp);
    setIsIntrestCalcLoading(false);
  }, [calculateInrestRateNotApr]);

  const handleAmortizedCalculate = useCallback(async (data: IAmorizationDetailer) => {
    setIsAmrotizationLoading(true);
    const resp: IAmorizationDetailerResponse = await calculateAmorizationDetailer(data);
    setAmortizationDetailerData(resp);
    setIsAmrotizationLoading(false);
  }, [calculateAmorizationDetailer]);

  return (
    <Modal
      open
      hideBackdrop
      resetPosition={calculatorModalPosition}
      dialogMinWidth='80vw'
      title="Calculators"
      onClose={onClose}
    >

      <Grid container spacing={3} gap={1} padding="0px 10px" marginTop={2}>
        <GridContentWrapper item xs={3}>
          <Formik enableReinitialize validationSchema={interestRateCalculatorValidation} initialValues={inrestRateCalcInitialValues} onSubmit={handleInrestRateCalculates}>
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <Grid container spacing={1}>
                  <Typography textAlign="center" variant="h3" marginBottom="15px" color={colorTheme.palette.common.black} >
                  Interest Rate Calculator - (Not Apr)
                  </Typography>
                  <CalculatorForm key='Interest' formConfig={intrestRateCalcNotAprFormConfig}>
                    <DealsButton type="submit" size="medium" loading={isIntrestCalcLoading}>
                      Calculate
                    </DealsButton>
                    <FieldDivWrapper>
                      {intrestRateData ? `% ${intrestRateData}` : ''}
                    </FieldDivWrapper>
                  </CalculatorForm>
                </Grid>
              </Form>
            )}
          </Formik>
        </GridContentWrapper>

        <GridContentWrapper item xs={8.9}>
          <Typography justifyContent="center" variant="h3" marginBottom="15px" color={colorTheme.palette.common.black} >
            Amortization Detailer
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Formik enableReinitialize validationSchema={amortizationDetailerValidation} initialValues={amortizationIntitialValues} onSubmit={handleAmortizedCalculate}>
                {(props) => (
                  <Form onSubmit={props.handleSubmit}>
                    <CalculatorForm key='amortization' formConfig={amortizationDetailerFormConfig}>
                      <DealsButton type="submit" size="medium" loading={isAmortizationLoading}>
                        Calculate
                      </DealsButton>
                    </CalculatorForm>
                  </Form>
                )}
              </Formik>
            </Grid>
            <Grid item xs={7.7} marginLeft={2} sx={{ overflowY: 'auto'}}>
              <Box sx={{ maxHeight: '30vh' }}>
                <List>
                  <CalculatorListItem secondaryAction={
                    <Typography>{formatCurrency(amortizationDetailerData?.totalInterest ?? 0)} </Typography>
                  }>
                    Total Interest No Additional Principal
                  </CalculatorListItem>
                  <CalculatorListItem
                    secondaryAction={
                      <Typography>{formatCurrency(amortizationDetailerData?.totalInterestPaid ?? 0)}</Typography>
                    }
                  >
                    Total Interest Paid
                  </CalculatorListItem>
                  <CalculatorListItem
                    secondaryAction={
                      <Typography>{amortizationDetailerData?.effectiveTerm}</Typography>
                    }
                  >
                    Effective Term
                  </CalculatorListItem>
                </List>
                <PhoenixGrid
                  noButton
                  strict
                  autoHeight
                  hideFooter
                  canNotEdit
                  canNotCopy
                  name=""
                  pageSize={amortizationDetailerData?.amortSchedules.length ?? 1}
                  rows={amortizationDetailerData?.amortSchedules ?? []}
                  getRowId={(row: IAmortSchedules) => (row.month ?? 0) as GridRowId}
                  columns={columns}
                />
              </Box>
            </Grid>
          </Grid>
        </GridContentWrapper>
      </Grid>
    </Modal>
  );
};
